import { useSyncExternalStore } from 'react';
import {
  canSelfRenew,
  describeSession,
  originOf,
  parseAuthTokenClaims,
  parseUrl,
  type AuthTokenClaims,
  type SessionFacts,
  type SessionStatus,
} from '@visious/core';
import { unregisterOwnSession } from '../api/auth';
import { systemClock } from './clock';
import { getPlatform } from './instance';

/**
 * Who is signed in, as one observable fact — R9's web adapter, one level above the token store.
 *
 * Three places need the answer and they must never disagree: the shell draws a padlock on three nav
 * items, those three pages render a gate instead of a feed, and the sign-in page reports the session.
 * Before this the shell read the token store directly in an effect, which was fine for one reader
 * and wrong for three — signing out would have updated the page that did it and left the header
 * claiming a session that no longer exists.
 *
 * So this is the router's shape rather than a hook's: a store with `subscribe`/`getSnapshot`, read
 * through `useSyncExternalStore`. The credential never enters a snapshot (R9) — what comes out is
 * `SessionFacts`, which core builds from the *claims*.
 */

/** Signed out, and not yet asked. `getPlatform()` is a fetch, so the truth arrives a tick late. */
const UNREADY: Session = {
  ready: false,
  facts: describeSession({ hasToken: false, claims: null, username: null, nowEpochSec: 0 }),
  grantedAtEpochSec: null,
};

export interface Session {
  /**
   * Whether the token store has actually been read. It matters because "not yet known" and "signed
   * out" render differently: a padlock drawn on every page load and then removed is a flash that
   * tells a signed-in reader they are locked out of their own app.
   */
  readonly ready: boolean;
  readonly facts: SessionFacts;
  /** When this browser received the grant. Local, and only ever shown next to the expiry. */
  readonly grantedAtEpochSec: number | null;
}

let snapshot: Session = UNREADY;
const listeners = new Set<() => void>();

function publish(next: Session): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** R4 — `JSON.parse` lives outside core; core reads the parsed value defensively. */
function claimsOf(token: string): AuthTokenClaims | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(token) as unknown;
  } catch {
    // Not JSON at all. The instance's own parser answers such a token with a leaked Crystal error at
    // `403`, so recognising it here saves a request and produces a sentence that names the problem.
    return null;
  }
  const claims = parseAuthTokenClaims(parsed);
  return claims.ok ? claims.value : null;
}

function signedOutAt(nowEpochSec: number): Session {
  return {
    ready: true,
    facts: describeSession({ hasToken: false, claims: null, username: null, nowEpochSec }),
    grantedAtEpochSec: null,
  };
}

async function read(): Promise<Session> {
  const boot = await getPlatform();
  /*
   * An instance that could not be resolved at all (§3's four steps all failed) is **signed out and
   * known**, not "not read yet". Returning `UNREADY` here would leave every gated page spinning on
   * "checking this browser for a session…" forever, since nothing else will ever publish. Signed out is
   * also the truthful reading: the token is keyed per instance origin, so with no instance there is no
   * credential — and the sign-in page the gate links to is the one place that explains the real problem
   * (`BOOT_COPY` in `screens/login/useLogin.ts`).
   */
  if (!boot.ok) return signedOutAt(Math.floor(systemClock.nowEpochMs() / 1000));

  const stored = boot.platform.tokens.read();
  const nowEpochSec = Math.floor(boot.platform.clock.nowEpochMs() / 1000);
  if (stored === null) return signedOutAt(nowEpochSec);

  return {
    ready: true,
    facts: describeSession({
      hasToken: true,
      claims: claimsOf(stored.token),
      username: stored.username,
      nowEpochSec,
    }),
    grantedAtEpochSec: stored.grantedAtEpochSec === 0 ? null : stored.grantedAtEpochSec,
  };
}

let refreshing: Promise<Session> | null = null;

/**
 * Re-read the credential and tell every reader.
 *
 * Deliberately **not** on a timer, and the limitation is worth stating: the expiry in a snapshot is
 * evaluated when the credential changes, when the sign-in page is opened, or when another tab writes
 * the key — not continuously. A token that ages out while a tab sits open therefore first shows up as
 * the classified `403` on the next authenticated call, which has copy of its own (`api/problem.ts`).
 * A one-minute interval would spend a wakeup for thirty days to catch a case that is already handled.
 */
export function refreshSession(): Promise<Session> {
  // Coalesced, because several components subscribe in the same tick and each would otherwise cause
  // its own read *and* its own publish — one render per subscriber for one unchanged fact.
  refreshing ??= read()
    .then((next) => {
      publish(next);
      return next;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/**
 * A refresh that must reflect a write that just happened — and it deliberately does **not** join an
 * in-flight one.
 *
 * That coalescing is a correctness bug for a writer, not just an optimisation: a read started before
 * the write took its value from the old credential, so joining it would resolve the write with the
 * *previous* session and publish "signed out" as the last word after a successful grant. Reachable
 * because the first subscriber's read and the callback's `storeGrant` both begin with
 * `await getPlatform()` and interleave. So: wait for whatever is in flight (its `finally` clears the
 * slot before this `then` runs) and then read again.
 */
function refreshAfterWrite(): Promise<Session> {
  const inFlight = refreshing;
  return inFlight === null ? refreshSession() : inFlight.then(() => refreshSession());
}

export interface Grant {
  readonly token: string;
  readonly username: string | null;
}

/**
 * Store a grant and publish the new session, answering with **the status that resulted** rather than
 * with "the write did not throw".
 *
 * The difference is a state a caller has to be able to see: `authStorage.write` swallows a quota or
 * blocked-context failure on purpose (a session that does not survive a reload is still a session),
 * and `getPlatform()` can fail outright — in both cases the credential is not there afterwards, and a
 * page that reported success would show a signed-in header over a session that cannot make one call.
 * `'signed-out'` coming back from a grant therefore means "this browser would not keep it".
 */
export async function storeGrant(grant: Grant): Promise<SessionStatus> {
  const boot = await getPlatform();
  if (!boot.ok) return 'signed-out';

  boot.platform.tokens.write({
    token: grant.token,
    username: grant.username,
    grantedAtEpochSec: Math.floor(boot.platform.clock.nowEpochMs() / 1000),
  });
  const next = await refreshAfterWrite();
  return next.facts.status;
}

/**
 * R9 — sign-out is local first, and server-side only when the grant itself allows it: the default
 * scope list omits `tokens*`, so an ordinary session cannot ask the instance to revoke itself. A
 * session that chose automatic renewal (2026-08-02) holds `POST:tokens*`, whose measured meaning
 * includes exactly one revocation — its own — so sign-out spends it. Best-effort and unawaited:
 * the local delete is the sign-out, and a refused revoke leaves a token that merely ages out.
 */
export async function signOutLocally(): Promise<void> {
  const boot = await getPlatform();
  if (boot.ok) {
    const stored = boot.platform.tokens.read();
    boot.platform.tokens.clear();
    if (stored !== null) {
      const claims = claimsOf(stored.token);
      if (claims !== null && canSelfRenew(claims.scopes)) {
        const session = sessionIdOf(stored.token);
        if (session !== null) void unregisterOwnSession(boot.platform, stored.token, session);
      }
    }
  }
  await refreshAfterWrite();
}

/** The session id off the raw credential — read here because the credential never enters core (R9). */
function sessionIdOf(token: string): string | null {
  try {
    const parsed: unknown = JSON.parse(token);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const session = (parsed as Record<string, unknown>)['session'];
    return typeof session === 'string' && session !== '' ? session : null;
  } catch {
    return null;
  }
}

export interface SessionInstance {
  readonly url: string;
  /** Scheme and authority — what the token is keyed by, and what a mismatch is compared on. */
  readonly origin: string;
  /** For display. The scheme is always https and the reader is looking for *which* instance. */
  readonly host: string;
}

/** The instance the credential is filed against, for the sign-in page's own copy. */
export async function sessionInstance(): Promise<SessionInstance | null> {
  const boot = await getPlatform();
  if (!boot.ok) return null;
  const url = boot.platform.config.instanceUrl;
  const origin = originOf(url);
  const host = parseUrl(url)?.host;
  if (origin === null || host === undefined) return null;
  return { url, origin, host };
}

/** For a screen that has to stamp a moment without reaching for a platform global itself. */
export function nowEpochSec(): number {
  return Math.floor(systemClock.nowEpochMs() / 1000);
}

/**
 * Fires with the new session on every publish — for a module that has to react to sign-in as well
 * as sign-out (`settingsSync.ts` pulls the account's preferences on the first). Unlike a React
 * subscriber it does not trigger the first read itself; callers that must see the initial session
 * pair this with {@link refreshSession}.
 */
export function onSessionChange(listener: (session: Session) => void): () => void {
  const check = (): void => listener(snapshot);
  listeners.add(check);
  return () => listeners.delete(check);
}

/**
 * Fires whenever the session **stops** being usable — this tab signing out, another tab signing out, or
 * a refresh finding the credential gone, expired or unreadable.
 *
 * It exists so that dropping the data a token paid for is not the sign-out *button's* job. A
 * review found that version: the eviction lived in the login page's handler, so the cross-tab path this
 * file goes out of its way to support (the `storage` listener below) published a signed-out session to a
 * tab whose query cache still held that account's feed. `main.tsx` owns the `QueryClient`, so it
 * subscribes here.
 */
export function onSessionLost(listener: () => void): () => void {
  let wasSignedIn = snapshot.facts.signedIn;
  const check = (): void => {
    const nowSignedIn = snapshot.facts.signedIn;
    if (wasSignedIn && !nowSignedIn) listener();
    wasSignedIn = nowSignedIn;
  };
  listeners.add(check);
  return () => listeners.delete(check);
}

/**
 * One `storage` listener for the whole document, installed on the first subscriber and never taken
 * down. It used to be one per *subscriber*, which made a single cross-tab write cost one full re-read
 * and one round of notifications **per subscriber** — see `platform/settings.ts`, where a grid made
 * that quadratic and froze the tab for seconds.
 */
let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs) return;
  watchingOtherTabs = true;
  /*
   * Another tab signing in or out is a real event, not a theoretical one: the token is keyed per
   * instance origin in `localStorage`, which is shared across every tab on this origin. Without this
   * a second tab keeps drawing an unlocked nav for a credential that has been deleted, and the reader
   * finds out by getting a 403 from a page that looked available.
   */
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith('visious.token.')) void refreshSession();
  });
}

function subscribe(onChange: () => void): () => void {
  watchOtherTabs();
  listeners.add(onChange);
  // First reader triggers the first read; later ones join the snapshot already published.
  if (!snapshot.ready) void refreshSession();
  return () => listeners.delete(onChange);
}

function getSnapshot(): Session {
  return snapshot;
}

/**
 * The session, re-read whenever it changes.
 *
 * `getSnapshot` returns the stored object rather than deriving one, which is why the facts are
 * computed in {@link refreshSession}: a snapshot rebuilt per call — with a fresh `nowEpochSec` in it —
 * would be a new object every render and `useSyncExternalStore` would loop forever.
 */
export function useSession(): Session {
  return useSyncExternalStore(subscribe, getSnapshot, () => UNREADY);
}
